import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';

const LOGO_URL = '/images/logo.svg';

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: t.nav.home },
    { to: '/gallery', label: t.nav.gallery },
    { to: '/services', label: t.nav.services },
    { to: '/sessoes-de-natal', label: t.christmas.navLabel, festive: true },
    // { to: '/vouchers', label: t.vouchers.navLabel }, // Temporarily hidden
    { to: '/about', label: t.nav.about },
    { to: '/contact', label: t.nav.contact },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? 'bg-warm-50/95 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="relative z-50" data-testid="nav-logo">
          <img
            src={LOGO_URL}
            alt="Vera Hilário Fotografia"
            className="h-16 md:h-20 w-auto"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.to.replace('/', '') || 'home'}`}
              className={`relative text-[0.9375rem] xl:text-base font-medium tracking-wide transition-colors duration-300 ${
                isActive(link.to)
                  ? 'text-accent'
                  : link.festive
                    ? 'text-accent hover:text-accent-dark'
                    : 'text-warm-700 hover:text-accent'
              }`}
            >
              {link.festive && <span aria-hidden="true">✦ </span>}
              {link.label}
              {isActive(link.to) && (
                <motion.span
                  layoutId="navbar-indicator"
                  className="absolute -bottom-1 left-0 right-0 h-0.5 bg-accent rounded-full"
                />
              )}
            </Link>
          ))}
        </div>

        {/* Right Side Actions */}
        <div className="hidden lg:flex items-center gap-4">
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            data-testid="language-toggle"
            className="flex items-center gap-2 text-warm-600 hover:text-accent transition-colors duration-300 text-base font-medium"
          >
            <Globe className="w-4 h-4" />
            <span>{language.toUpperCase()}</span>
          </button>

          {/* Book Button */}
          <Link to="/contact#booking" data-testid="nav-book-btn">
            <Button className="bg-accent hover:bg-accent-dark text-white font-medium px-6 rounded-full transition-all duration-300 hover:shadow-lg">
              {t.nav.book}
            </Button>
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="lg:hidden relative z-50 p-2"
          data-testid="mobile-menu-toggle"
        >
          {isOpen ? (
            <X className="w-6 h-6 text-warm-800" />
          ) : (
            <Menu className="w-6 h-6 text-warm-800" />
          )}
        </button>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-warm-50/98 backdrop-blur-lg lg:hidden z-40"
            >
              <div className="flex flex-col items-center justify-center h-full gap-8">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.to}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={link.to}
                      data-testid={`mobile-nav-${link.to.replace('/', '') || 'home'}`}
                      className={`text-2xl font-medium ${
                        isActive(link.to) || link.festive ? 'text-accent' : 'text-warm-700'
                      }`}
                    >
                      {link.festive && <span aria-hidden="true">✦ </span>}
                      {link.label}
                    </Link>
                  </motion.div>
                ))}

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="flex flex-col items-center gap-6 mt-8"
                >
                  <button
                    onClick={toggleLanguage}
                    data-testid="mobile-language-toggle"
                    className="flex items-center gap-2 text-warm-600 text-lg font-medium"
                  >
                    <Globe className="w-5 h-5" />
                    <span>{language === 'pt' ? 'Português' : 'English'}</span>
                  </button>

                  <Link to="/contact#booking" data-testid="mobile-book-btn">
                    <Button className="bg-accent hover:bg-accent-dark text-white font-medium px-8 py-3 rounded-full text-base">
                      {t.nav.book}
                    </Button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
}
